import {Desk} from "lib/commons/desk";
import {ILastDatasHolder, LASTDATAS} from "lib/commons/lastDatas";
import {REG} from 'lib/commons/registry';
import {IBasicUniversePointer} from "lib/core/universe";
import {account, AuthSrv, JUser} from "lib/core/user";

/**
 * Feature de mémorisation des données graphiques pour restauration de la page à l'identique.
 * Pour isoler les données par user, le AuthServer utilisé est REG.reg.env.universe.auth.
 * Si ce AuthServer n'est pas présent, les données sont stockés avec un user anonyme vide ''.
 * Les données sont stockées dans le localStorage.
 * 1- Au déchargement de la page, mémorise le dernier état via LASTDATAS.buildLastDatas() dans le localStorage avec la key `lastDatas:desk:${desk.name}:${account}:${ldSession}`
 * 2- Au chargement, initialise un IUserDatas sur REG.reg.env.userDatas en retournant en priorité
 *      - hash de l'URL, si le UrlHashObjDeskFeat est activé (DOIT être activé avant)
 *      - les lastDatas du user courant pour le desk courant stocks dans le localStorage.
 */
export class LastDatasDeskFeat extends Desk {

	/** Peut être ajouté après desk.launch(), mais impliquera un rebuild graphique. */
	static add(desk: Desk, auth?: AuthSrv): LastDatasDeskFeat {
		if (this.isIn(desk)) return desk;
		const d = desk as LastDatasDeskFeat;
		d.assignProps(this);
		d.ldSession = "";

		REG.reg.addToList(Desk.LC_init, 'lastDatas', 1, () => {
			d.maxUserSessions = 10;

			if (!(document.documentElement as ILastDatasHolder).getParentLastDatas) {
				(document.documentElement as ILastDatasHolder).getParentLastDatas = () => (desk as LastDatasDeskFeat).lastDatas;
			}

			//Recherche du AuthServer
			if (auth === undefined) auth = (REG.reg.env as IBasicUniversePointer).universe?.auth || null;

			//Chargement des lastDatas
			d._fetchLastDatas(auth);

			if (auth) auth.listeners.on('loggedUserChanged', (oldUser: JUser, newUser: JUser) => {
				if (oldUser) d.saveLastDatas(oldUser.account);
				d._fetchLastDatas(auth);
			}, -10); //avant les déchargements graphiques pour saveLastDatas et les rechargements.

			if (Desk.electron) {
				// TODO cas spécifique pour contourner un bug d'Electron sur le visibility change
				// TODO cf https://github.com/electron/electron/issues/24630
				window.addEventListener('beforeunload', () => {
					if (!auth || auth.currentUser) d.saveLastDatas(auth?.currentAccount || '');
				});
			} else {
				document.addEventListener("visibilitychange", () => {
					if (document.visibilityState === "hidden" && (!auth || auth.currentUser)) {
						//console.log("document.visibilityState === \"hidden\"", document.visibilityState === "hidden");
						//On enregistre les lastDatas si page web hors auth, ou si un user est bien présent (y compris anonymous).
						d.saveLastDatas(auth?.currentAccount || '');
					}
				});
			}
		}, 0 /* après le check de l'auth du user courant (-1000) */);

		return d;
	}

	/** Dernières données chargées. */
	lastDatas: Jsonisable;

	/**
	 * Code de la session courante : tous les onglets d'un même navigateur exploitant le même desk.name
	 * devraient posséder un code de session différent pour ne pas écraser les lastDatas.
	 * La définition de cette session n'est pas gérée par cette feature. cf AppFrameDeskFeat pour un exemple.
	 */
	ldSession: string;

	protected _fetchLastDatas(auth: AuthSrv) {
		try {
			const v = localStorage.getItem(`lastDatas:desk:${this.name}:${auth?.currentAccount || ''}:${this.ldSession}`);
			this.lastDatas = v ? JSON.parse(v) : null;
		} catch (e) {
			this.lastDatas = null;
		}
	}

	/** Nb max de couple user-session supplémentaires pour lesquels des données sont stockées. */
	maxUserSessions: number;

	/** */
	saveLastDatas(user: account) {
		const datas = LASTDATAS.buildDocumentLastDatas();
		const key = `lastDatas:desk:${this.name}`;
		localStorage.setItem(`${key}:${user}:${this.ldSession}`, JSON.stringify(datas));
		//cleanup des données
		let mapUsers: { u: string, t: number, s: string }[];
		const map = localStorage.getItem(key);
		if (map) {
			mapUsers = JSON.parse(map);
			//On suppr les entrée de notre user (qui sera rajouté à nouveau après).
			mapUsers = mapUsers.filter((e) => e.u !== user || e.s !== this.ldSession);
			while (mapUsers.length > this.maxUserSessions) {
				//On cleanup les plus anciens enregistrements au delà de this.maxUserSessions.
				localStorage.removeItem(key + ":" + mapUsers[0].u);
				mapUsers.splice(0, 1);
			}
		} else {
			mapUsers = [];
		}
		mapUsers.push({u: user, t: Date.now(), s: this.ldSession});
		localStorage.setItem(key, JSON.stringify(mapUsers));
	}

	static isIn(desk: Desk): desk is LastDatasDeskFeat {return '_fetchLastDatas' in desk}

}
